import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  styled,
} from "@mui/material";
import { useNotify } from "react-admin";
import { settings } from "app";
import { tenantLoginConfig } from "utils/tenants";
import { accountRequests } from "api/accountApi";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";
import { applySessionAndLoadUser } from "auth/security/authProvider";
import { getAccessToken, redirectAfterLogin, removeTokens, removeUser } from "auth/security/securityUtils";

const StyledPage = styled(Box)(({ background }) => ({
  backgroundImage: background,
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
}));

const StyledCard = styled(Card)({
  backgroundColor: "rgba(255, 255, 255)",
  width: "500px",
  maxWidth: "95vw",
  borderRadius: "4px",
});

/**
 * Shown right after login when the employee holds several node/position profiles: the backend session is
 * "pending" (no roles) until one is chosen through POST account/security/profiles/switch.
 */
const SelectProfilePage = () => {
  const config = tenantLoginConfig[settings.tenant];
  const notify = useNotify();
  const notifyError = useNotifyError();

  const [profiles, setProfiles] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Binds the pending session to a profile: the backend answers with a new, full session that replaces it.
  const switchTo = useCallback(async profile => {
    const { data } = await dataProvider.customRequest(
      accountRequests.postSwitchProfile(),
      { nodeId: profile.nodeId, positionId: profile.positionId },
      "POST",
    );
    await applySessionAndLoadUser(data.sessionId);
    redirectAfterLogin();
  }, []);

  useEffect(() => {
    if (!getAccessToken()) {
      window.location.href = "/login";
      return;
    }

    const load = async () => {
      try {
        const { data } = await dataProvider.customRequest(accountRequests.getProfiles(), null, "GET");
        const available = data?.enabled ? (data.profiles ?? []) : [];

        // The pending session grants nothing, so there is no login to "finish" without a profile: a single one is
        // bound straight away, none at all (feature switched off / Employee Service failure) leaves only "Изход".
        if (available.length === 1) {
          await switchTo(available[0]);
          return;
        }
        if (available.length === 0) {
          notify("Няма наличен профил за вход. Опитайте отново или излезте.", { type: "error" });
          setProfiles([]);
          return;
        }

        setProfiles(available);
        setSelected(available[0]);
      } catch (error) {
        notify("Профилите не можаха да бъдат заредени. Опитайте да влезете отново.", { type: "error" });
        setProfiles([]);
      }
    };

    load();
  }, [switchTo, notify]);

  const handleContinue = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await switchTo(selected);
    } catch (error) {
      notifyError(error);
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    removeTokens();
    removeUser();
    window.location.href = "/login";
  };

  const isSelected = profile => selected?.nodeId === profile.nodeId && selected?.positionId === profile.positionId;

  return (
    <StyledPage background={config.background}>
      <StyledCard>
        <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box sx={{ m: 2, height: "110px", scale: 0.9, background: config.logoBackground, p: 1, borderRadius: "4px" }}>
            <img
              src={config.logo}
              alt="university-logo"
              style={{ width: "100%", height: "100%" }}
            />
          </Box>
          <Typography sx={{ textAlign: "center", fontWeight: "bold", color: config.titleColor }}>
            Изберете профил
          </Typography>
          <Typography
            color={config.subTitleColor}
            sx={{ mt: 1, mb: 2, textAlign: "center" }}
          >
            Заемате няколко длъжности. Изберете структурата и длъжността, с които да влезете в системата.
          </Typography>

          {profiles === null ? (
            <CircularProgress sx={{ my: 4 }} />
          ) : (
            <List sx={{ width: "100%" }}>
              {profiles.map(profile => (
                <ListItemButton
                  key={`${profile.nodeId}-${profile.positionId}`}
                  selected={isSelected(profile)}
                  onClick={() => setSelected(profile)}
                  sx={{ borderRadius: "4px", mb: 1, border: "1px solid rgba(0, 0, 0, 0.12)" }}
                >
                  <ListItemText
                    primary={profile.nodeName}
                    secondary={profile.positionName}
                  />
                  {profile.isManager && (
                    <Chip
                      label="Ръководител"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </ListItemButton>
              ))}
            </List>
          )}

          <Button
            sx={{
              mt: 2,
              background: config.buttonColor,
              "&:hover": { background: config.buttonHoverColor },
            }}
            fullWidth
            variant="contained"
            disabled={!selected || submitting || profiles === null}
            onClick={handleContinue}
          >
            Продължи
          </Button>
          <Button
            sx={{ mt: 1 }}
            fullWidth
            variant="text"
            color="inherit"
            onClick={handleLogout}
          >
            Изход
          </Button>
        </CardContent>
      </StyledCard>
    </StyledPage>
  );
};

export default SelectProfilePage;
